/**
 * Realtime helpers para tareas.
 *
 * Patrón: el callback solo dispara invalidaciones de TanStack Query.
 * No metemos los rows directo en cache para evitar drift con RLS — preferimos
 * un refetch limpio que sabemos que respeta la policy en el servidor.
 */
import { supabase } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type TaskRealtimeKind = 'task' | 'assignment' | 'comment' | 'activity';

export interface TaskRealtimeEvent {
  kind: TaskRealtimeKind;
  table: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  taskId: string | null;
}

/**
 * Suscribe a INSERT/UPDATE/DELETE de las 4 tablas core.task_* (las que están
 * en la publication supabase_realtime). El filtrado por user lo hace RLS:
 * el servidor no nos manda eventos de filas que no podemos ver.
 *
 * Devuelve el channel para que el caller pueda `channel.unsubscribe()` en cleanup.
 */
export function subscribeToTasks(
  onEvent: (event: TaskRealtimeEvent) => void,
): RealtimeChannel {
  const channel = supabase.channel('tasks-realtime');

  const subscribe = (
    table: 'tasks' | 'task_assignments' | 'task_comments' | 'task_activity',
    kind: TaskRealtimeKind,
  ) => {
    // supabase-js infiere muy laxo el tipo de 'postgres_changes' — casteamos
    // el channel a any solo para esta línea de subscribe.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (channel as any).on(
      'postgres_changes',
      { event: '*', schema: 'core', table },
      (payload: {
        eventType: 'INSERT' | 'UPDATE' | 'DELETE';
        new: Record<string, unknown> | null;
        old: Record<string, unknown> | null;
      }) => {
        const row = payload.new ?? payload.old ?? {};
        const taskId =
          (row.task_id as string | undefined) ??
          (row.id as string | undefined) ??
          null;
        onEvent({ kind, table, eventType: payload.eventType, taskId });
      },
    );
  };

  subscribe('tasks', 'task');
  subscribe('task_assignments', 'assignment');
  subscribe('task_comments', 'comment');
  subscribe('task_activity', 'activity');

  channel.subscribe();
  return channel;
}
